import { create } from 'zustand';

// Client-side UI state for the floating MFA device cards: which devices have a
// card open, their front-to-back stacking order, and which card is focused.
// Nothing here talks to the backend — each card fetches its own data through
// routes/mfa/hooks/use-mfa-connection.
//
// A panel is `{ deviceId, z }`, where `z` is its CSS z-index. We hand out an
// ever-increasing z-index so the most recently touched card renders on top.
// The base sits above the app chrome (main menu 100, right pane 15) and below
// react-select's dropdown (10000000).
let lastZIndex = 1050;
const nextZIndex = () => { lastZIndex += 1; return lastZIndex; };

// Return `panels` with `deviceId` moved to the front of the stack.
const bringToFront = (panels, deviceId) =>
  panels.map((panel) => (panel.deviceId === deviceId ? { ...panel, z: nextZIndex() } : panel));

const useMfaPanelStore = create((set) => ({
  panels: [],           // open cards, one per device
  activeDeviceId: null, // the focused card (the one that reacts to paste)

  // Open a card for a device — or just focus it if it's already open.
  openPanel: (deviceId) => set((state) => {
    const isOpen = state.panels.some((panel) => panel.deviceId === deviceId);
    const panels = isOpen
      ? bringToFront(state.panels, deviceId)
      : [...state.panels, { deviceId, z: nextZIndex() }];

    return { panels, activeDeviceId: deviceId };
  }),

  // Close a card. If it was focused, hand focus to whatever is now on top.
  closePanel: (deviceId) => set((state) => {
    const panels = state.panels.filter((panel) => panel.deviceId !== deviceId);
    const topPanel = panels[panels.length - 1];
    const activeDeviceId = state.activeDeviceId === deviceId
      ? (topPanel ? topPanel.deviceId : null)
      : state.activeDeviceId;

    return { panels, activeDeviceId };
  }),

  // Bring a card to the front and focus it (on click, or when a drag starts).
  focusPanel: (deviceId) => set((state) => {
    if (state.activeDeviceId === deviceId) return state; // already focused

    return {
      panels: bringToFront(state.panels, deviceId),
      activeDeviceId: deviceId
    };
  })
}));

export default useMfaPanelStore;
